// The Capablity KEYS!
const KBGP = 'kbgp';
const KDNS = 'kdns';
const KPROXY = 'kproxy'; // still behind a perm
const KSNMPTRAP = 'ksnmptrap'; // only behind a perm
const KSYSLOG = 'ksyslog'; // only behind a perm
const RANGER = 'ranger';
const KTRAFFIC = 'ktraffic';

// Block from public!
const LIVESYNTH = 'livesynth';
const KPACKET = 'kpacket';
const KSQUEEGEE = 'ksqueegee';
const KMETA = 'kmeta';

// NOT YET but planned
const KSYNTH = 'ksynth';

const KDUMMY = 'kdummy';
const KPTR = 'kptr';

// Defines the default configuration for each capability. When installing a capability, a new config object will be created with the args set in this map.
const CAPABILITIES_DEFAULT_CONFIGS = {
  [KDNS]: {
    args: {
      interface: 'any',
      fanoutCount: '4',
      enableCompression: true,
      promiscuous: false,
      sampleRate: 1
    }
  },
  [KPROXY]: {
    args: {
      host: '0.0.0.0',
      port: 9995,
      basePort: 40010,
      sflowAgentAddress: false,
      logLevel: 'info'
    }
  },
  [KSNMPTRAP]: {
    args: {}
  },
  [KTRAFFIC]: {
    args: {}
  },
  [KSYSLOG]: {
    args: {
      udpListenAddr: '0.0.0.0:514',
      tcpListenAddr: '0.0.0.0:514'
    }
  },
  [KSQUEEGEE]: {
    args: {}
  },
  [KSYNTH]: {
    args: {}
  },
  [KMETA]: {
    args: {}
  },
  [KPTR]: {
    args: {}
  },
  [KDUMMY]: {
    args: {}
  }
};

const CMD_ARGS_KPROXY_KEBAB_OBJ = ['proxyhttp', 'proxyhttpallow'];
const CMD_ARGS_KPROXY_KEBAB = ['-proxy-http', '-proxy-http-allow'];

const CAPABILITY_TITLES = {
  kbgp: 'BGP Proxy (AKA kbgp)',
  kdummy: 'Dummy Capability',
  kdns: 'DNS OTT Tap',
  kproxy: 'Flow Proxy (Edge Processing)',
  ksnmptrap: 'SNMP Trap Receiver',
  ksynth: 'Synthetics',
  ksyslog: 'Syslog Server',
  livesynth: 'Connectivity Test',
  ktraffic: 'Flow Proxy (Forwarding)',
  ranger: 'SNMP/ST',
  ksqueegee: 'Device Configuration Sync',
  kptr: 'Reverse DNS',
  kmeta: 'Metadata Collector'
};

const CAPABILITY_DESCRIPTIONS = {
  kbgp: 'Proxy local iBGP Route Reflector Client session from multiple hosts and multiplex them securely to Kentik SaaS cluster',
  kdns: 'Upon installation on a DNS server, collect and export DNS Query Responses to match against Flow Telemetry for OTT detection',
  kproxy: 'Decodes, enriches (SNMP/BGP/RPKI), and samples flow at the edge before forwarding to Kentik.',
  ksnmptrap: 'Receive SNMP Traps and securely transport them to Kentik',
  ksynth: 'Turns host into Kentik Synthetics Agent to perform Network, HTTP and DNS synthetic tests',
  ksyslog: 'Provides syslog server that transports logs to Kentik',
  ranger: 'Base layer of network visibility including up/down, interface stats, and hardware components',
  livesynth: 'Connectivity Test - Allows AI Advisor to run network tests (ping,trace) on demand',
  ktraffic: 'Lightweight flow forwarding to Kentik with minimal local processing. SaaS-side enrichment in development.',
  ksqueegee: 'Fetch and backup configuration from network devices',
  kptr: 'Resolves reverse DNS (PTR) lookups for flow data enrichment',
  kmeta: 'Fetch metadata using custom collectors and updates custom dimensions/populators',
  kdummy: 'Test Capability'
};

// Flow Proxy is presented to users as a single capability with two delivery modes, even
// though `ktraffic` and `kproxy` remain distinct capability keys/entities everywhere else.
const FLOW_PROXY_TITLE = 'Flow Proxy';
const FLOW_PROXY_KEYS = [KTRAFFIC, KPROXY];
const FLOW_PROXY_MODES = {
  forwarding: 'forwarding',
  compatibility: 'compatibility' // "Edge Processing" mode (kproxy)
};
const FLOW_PROXY_MODE_LABELS = {
  [FLOW_PROXY_MODES.forwarding]: 'Forwarding',
  [FLOW_PROXY_MODES.compatibility]: 'Edge Processing'
};
const FLOW_PROXY_MODE_CAPABILITY_KEY = {
  [FLOW_PROXY_MODES.forwarding]: KTRAFFIC,
  [FLOW_PROXY_MODES.compatibility]: KPROXY
};
const FLOW_PROXY_COMBINED_DESCRIPTION = [
  `Forwarding Mode: ${CAPABILITY_DESCRIPTIONS[KTRAFFIC]}`,
  `Edge Processing Mode: ${CAPABILITY_DESCRIPTIONS[KPROXY]}`
].join('\n\n');

const isFlowProxyCapabilityKey = (capabilityKey: string) => FLOW_PROXY_KEYS.includes(capabilityKey);

const getFlowProxyModeForCapabilityKey = (capabilityKey: string) =>
  capabilityKey === KPROXY ? FLOW_PROXY_MODES.compatibility : FLOW_PROXY_MODES.forwarding;

const getCapabilityKeyForFlowProxyMode = (mode: string) => FLOW_PROXY_MODE_CAPABILITY_KEY[mode] || KTRAFFIC;

const getFlowProxyModeTitle = (mode: string) => `${FLOW_PROXY_TITLE}: ${FLOW_PROXY_MODE_LABELS[mode]}`;

/**
 * Collapses `ktraffic`/`kproxy` into a single representative key for display purposes, when
 * both are present in the given list (e.g. both permitted for the current company/user).
 * If only one (or neither) is present, the list is returned unchanged since there is nothing
 * to combine into a single "Flow Proxy" entry.
 *
 * Prefers whichever engine is actually *enabled* over mere presence in
 * `installedAgentCapabilities` - disabling (not uninstalling) a capability leaves its record
 * present with `isEnabled: false`, so presence alone can't tell which mode is active.
 * @param installedAgentCapabilities - keyed by capability name; used to prefer
 *   whichever engine is actually enabled (or, failing that, installed) as the representative key.
 */
const collapseFlowProxyCapabilityKeys = (
  capabilityKeys: string[],
  installedAgentCapabilities: Record<string, { isEnabled?: boolean } | undefined> = {}
): string[] => {
  const hasKtraffic = capabilityKeys.includes(KTRAFFIC);
  const hasKproxy = capabilityKeys.includes(KPROXY);

  if (!hasKtraffic || !hasKproxy) {
    return capabilityKeys;
  }

  const ktrafficCap = installedAgentCapabilities?.[KTRAFFIC];
  const kproxyCap = installedAgentCapabilities?.[KPROXY];

  let representativeKey: string = KTRAFFIC;
  if (kproxyCap?.isEnabled && !ktrafficCap?.isEnabled) {
    representativeKey = KPROXY;
  } else if (!ktrafficCap && kproxyCap) {
    representativeKey = KPROXY;
  }

  return [...capabilityKeys.filter((key) => key !== KTRAFFIC && key !== KPROXY), representativeKey];
};

const CLUSTER_HEALTH_STATE_UNSPECIFIED = 'CLUSTER_HEALTH_STATE_UNSPECIFIED';
const CLUSTER_HEALTH_STATE_HEALTHY = 'CLUSTER_HEALTH_STATE_HEALTHY';
const CLUSTER_HEALTH_STATE_WARNING = 'CLUSTER_HEALTH_STATE_WARNING';
const CLUSTER_HEALTH_STATE_DEGRADED = 'CLUSTER_HEALTH_STATE_DEGRADED';
const CLUSTER_HEALTH_STATE_CRITICAL = 'CLUSTER_HEALTH_STATE_CRITICAL';
const CLUSTER_HEALTH_STATE_DOWN = 'CLUSTER_HEALTH_STATE_DOWN';

const CLUSTER_HEALTH_LABELS = {
  CLUSTER_HEALTH_STATE_UNSPECIFIED: 'Unspecified',
  CLUSTER_HEALTH_STATE_HEALTHY: 'Healthy',
  CLUSTER_HEALTH_STATE_UNHEALTHY: 'Unhealthy', // deprecated: remove once backend stops sending UNHEALTHY
  CLUSTER_HEALTH_STATE_WARNING: 'Warning',
  CLUSTER_HEALTH_STATE_DEGRADED: 'Degraded',
  CLUSTER_HEALTH_STATE_CRITICAL: 'Critical',
  CLUSTER_HEALTH_STATE_DOWN: 'Down'
};

const CLUSTER_HEALTH_INTENTS = {
  CLUSTER_HEALTH_STATE_UNSPECIFIED: 'none',
  CLUSTER_HEALTH_STATE_HEALTHY: 'success',
  CLUSTER_HEALTH_STATE_UNHEALTHY: 'danger', // deprecated: remove once backend stops sending UNHEALTHY
  CLUSTER_HEALTH_STATE_WARNING: 'warning',
  CLUSTER_HEALTH_STATE_DEGRADED: 'severe',
  CLUSTER_HEALTH_STATE_CRITICAL: 'danger',
  CLUSTER_HEALTH_STATE_DOWN: 'critical'
};

const GROUP_HEALTH_STATE_UNSPECIFIED = 'GROUP_HEALTH_STATE_UNSPECIFIED';
const GROUP_HEALTH_STATE_HEALTHY = 'GROUP_HEALTH_STATE_HEALTHY';
const GROUP_HEALTH_STATE_WARNING = 'GROUP_HEALTH_STATE_WARNING';
const GROUP_HEALTH_STATE_DEGRADED = 'GROUP_HEALTH_STATE_DEGRADED';
const GROUP_HEALTH_STATE_CRITICAL = 'GROUP_HEALTH_STATE_CRITICAL';
const GROUP_HEALTH_STATE_DOWN = 'GROUP_HEALTH_STATE_DOWN';

const GROUP_HEALTH_LABELS = {
  GROUP_HEALTH_STATE_UNSPECIFIED: 'Unspecified',
  GROUP_HEALTH_STATE_HEALTHY: 'Healthy',
  GROUP_HEALTH_STATE_UNHEALTHY: 'Unhealthy', // deprecated: remove once backend stops sending UNHEALTHY
  GROUP_HEALTH_STATE_WARNING: 'Warning',
  GROUP_HEALTH_STATE_DEGRADED: 'Degraded',
  GROUP_HEALTH_STATE_CRITICAL: 'Critical',
  GROUP_HEALTH_STATE_DOWN: 'Down'
};

const GROUP_HEALTH_INTENTS = {
  GROUP_HEALTH_STATE_UNSPECIFIED: 'none',
  GROUP_HEALTH_STATE_HEALTHY: 'success',
  GROUP_HEALTH_STATE_UNHEALTHY: 'danger', // deprecated: remove once backend stops sending UNHEALTHY
  GROUP_HEALTH_STATE_WARNING: 'warning',
  GROUP_HEALTH_STATE_DEGRADED: 'severe',
  GROUP_HEALTH_STATE_CRITICAL: 'danger',
  GROUP_HEALTH_STATE_DOWN: 'critical'
};

const SYNTHETICS = 'Synthetics';

const NOT_AUTHORIZED = 'Not Authorized';
const RUNNING = 'Running';
const DOWN = 'Down';
const DISABLED = 'Disabled';
const WARNING = 'Warning';
const DEGRADED = 'Degraded';
const CRITICAL = 'Critical';
const UP = 'Up';
const PENDING = 'Pending';
const UNKNOWN = 'Unknown';

const AGENT_HEALTH_LABELS = {
  AGENT_HEALTH_STATE_HEALTHY: 'Healthy',
  AGENT_HEALTH_STATE_WARNING: 'Warning',
  AGENT_HEALTH_STATE_DEGRADED: 'Degraded',
  AGENT_HEALTH_STATE_CRITICAL: 'Critical',
  AGENT_HEALTH_STATE_DOWN: 'Down'
};

const AGENT_HEALTH_INTENTS = {
  AGENT_HEALTH_STATE_HEALTHY: 'success',
  AGENT_HEALTH_STATE_WARNING: 'warning',
  AGENT_HEALTH_STATE_DEGRADED: 'severe',
  AGENT_HEALTH_STATE_CRITICAL: 'danger',
  AGENT_HEALTH_STATE_DOWN: 'critical'
};

const CAPABILITY_HEALTH_LABELS = {
  CAPABILITY_HEALTH_STATE_HEALTHY: 'Healthy',
  CAPABILITY_HEALTH_STATE_WARNING: 'Warning',
  CAPABILITY_HEALTH_STATE_DEGRADED: 'Degraded',
  CAPABILITY_HEALTH_STATE_CRITICAL: 'Critical',
  CAPABILITY_HEALTH_STATE_DOWN: 'Down',
  CAPABILITY_HEALTH_STATE_DISABLED: 'Disabled',
  CAPABILITY_HEALTH_STATE_UNSPECIFIED: 'Unknown'
};

const CAPABILITY_HEALTH_INTENTS = {
  CAPABILITY_HEALTH_STATE_HEALTHY: 'success',
  CAPABILITY_HEALTH_STATE_WARNING: 'warning',
  CAPABILITY_HEALTH_STATE_DEGRADED: 'severe',
  CAPABILITY_HEALTH_STATE_CRITICAL: 'danger',
  CAPABILITY_HEALTH_STATE_DOWN: 'critical',
  CAPABILITY_HEALTH_STATE_DISABLED: 'none',
  CAPABILITY_HEALTH_STATE_UNSPECIFIED: 'none'
};

// Found in https://github.com/kentik/chf/tags
const KPROXY_VERSION_MAP: Record<string, { version: string; builtOn: string }> = {
  af21606802e6f95db00f398be9ab5b8709c537dc: { version: '7.52', builtOn: '2025-06-30' },
  '170642f576a60bfd78a19fe6993a660990826537': { version: '7.51', builtOn: '2025-04-29' },
  d9d0625d99348f002a7cee7dbe93458012dd7c61: { version: '7.50', builtOn: '2025-02-05' },
  '30326df8c3aaea90c1d04d3c85e3f73ccf9ddc2c': { version: '7.49', builtOn: '2025-01-10' },
  d12218520e8b772c7c703cf8905952cd8fe45049: { version: '7.48', builtOn: '2024-09-23' },
  e98c6e2bb1ec675ef07ad1672bc27f79ffcadbaa: { version: '7.47', builtOn: '2024-06-20' },
  '7cf99320adcf64b4d690eb48a7c06a5f3f644dfc': { version: '7.46', builtOn: '2024-06-06' },
  cea5cac0256e10d5635169d7e275b4be766dbaa4: { version: '7.45', builtOn: '2024-05-20' },
  '5da1f9cc4c1bf78a3856a528f747a76e27e35e44': { version: '7.44', builtOn: '2024-02-13' },
  a6c083e1b5a7669c6de2dc4ceabfe38f05313174: { version: '7.43', builtOn: '2024-02-09' },
  e12760287688b3b5fd706d740d0ee9648aa6e427: { version: '7.42', builtOn: '2024-01-02' },
  dd6cf3125f93df146c2f6d8539e40b49b21119a7: { version: '7.41', builtOn: '2023-10-19' },
  deedb7cada6fe9e062e0b073c11aa003901e2168: { version: '7.40', builtOn: '2023-08-17' },
  '7e6d5714052b5ff536bc4a6716925c8bf61c9668': { version: '7.39', builtOn: '2023-07-26' },
  c03087b8d6e506a80febe02150893fb8eebb25b4: { version: '7.38', builtOn: '2023-02-13' },
  '7bceccd84c3e5cd268ae49f4aa247e1d0ed24e63': { version: '7.37', builtOn: '2022-11-10' },
  '3bb2dc88f141eb2121bf8c4c1dd32b1a12a1d1e7': { version: '7.36', builtOn: '2022-08-25' },
  d130af1ee2b2b012e978c8a8da6cc00e55c500cf: { version: '7.35', builtOn: '2022-08-03' },
  cf546ed24afbb0349ecc4b181de402db5fddd556: { version: '7.34', builtOn: '2022-07-29' },
  '520ae188047f57795728056c79099ce406083760': { version: '7.33', builtOn: '2022-05-11' },
  '9d3aadde2ce3616aeb664d59a132cfcc3724d39a': { version: '7.32', builtOn: '2022-03-04' },
  '70363dfb51ba28de129d803a53c7e49130ee9bee': { version: '7.31', builtOn: '2021-09-09' },
  ddfe0dd5b30574783e2883e92adf822a704bb905: { version: '7.30', builtOn: '2021-08-30' },
  '7490cb0507ba3a28a08a72fcd374d864cbc523ea': { version: '7.29', builtOn: '2021-01-12' },
  dfd3c34bc551a0064bef2644928d5649596d4e3e: { version: '7.28', builtOn: '2020-12-15' },
  ae4d20a3ffbd95b320b275271aa50419adc14e0c: { version: '7.27', builtOn: '2020-11-20' },
  c7f723ed85fa1fb912e14bf6d12cdffaea6407fc: { version: '7.26', builtOn: '2020-10-28' },
  f2497c737fe7fd32f2f4566c8c64a49544a0552a: { version: '7.25', builtOn: '2020-09-09' },
  bf0769173736564966ba5a47e707cecacc3aae6f: { version: '7.24', builtOn: '2020-09-03' },
  '1d53fd3d74c4d5d880387214d9e1d647f893d08d': { version: '7.22', builtOn: '2020-07-31' },
  '878ce2d512aaad9890b799ab61b048f53bb8f7c1': { version: '7.21', builtOn: '2020-05-28' },
  '069c35e82356bc64d06b1f5470a18cdae081cb47': { version: '7.20', builtOn: '2020-05-21' },
  '44cc94009d0e4f03e2a9a9e70a80af2f708be7f8': { version: '7.19', builtOn: '2020-04-30' },
  '0ab7fb1d5e04264a9cf141b4728e6fcf356b99bb': { version: '7.18', builtOn: '2020-04-17' },
  ba30c176a8b83fdf4533648820091d96879f3773: { version: '7.17', builtOn: '2020-04-12' },
  '3e3ce061aa1666de539094a43f0cf86825e82d4d': { version: '7.16', builtOn: '2020-04-09' },
  '1.0.200408.522.67b814b944.master': { version: '7.14', builtOn: '2020-04-08' },
  'dirty-eb9a47b2b1fb6b33cc57c722772075538448c2a0': { version: '7.11', builtOn: '2020-03-13' },
  '46dd56cd6cbbf7eecb0ef6c28fe4f9763f755b19': { version: '7.10', builtOn: '2020-01-29' },
  '3ea1cdd706e3df94a836347158107040bfaea106': { version: '7.9', builtOn: '2019-12-20' },
  '573f197ce208caefd9b827200f7f56475cb637a1': { version: '7.8', builtOn: '2019-11-26' },
  '96d91ca7dc88c11f569a5871cb4d7cfaaccaaf5d': { version: '7.7', builtOn: '2019-11-22' },
  '0845eb8731b256daf3cb4adccb12fb9b37712c59': { version: '7.6', builtOn: '2019-10-24' },
  a2ea87408375e7c891e086fb2c62dd3c04c96b2f: { version: '7.5', builtOn: '2019-10-03' }
};

const KPROXY_DISPLAY_INTERVAL = 60 * 60 * 1000;

const AGENT_HEALTH = {
  AGENT_HEALTH_STATE_HEALTHY: 'AGENT_HEALTH_STATE_HEALTHY',
  AGENT_HEALTH_STATE_WARNING: 'AGENT_HEALTH_STATE_WARNING',
  AGENT_HEALTH_STATE_DEGRADED: 'AGENT_HEALTH_STATE_DEGRADED',
  AGENT_HEALTH_STATE_CRITICAL: 'AGENT_HEALTH_STATE_CRITICAL',
  AGENT_HEALTH_STATE_DOWN: 'AGENT_HEALTH_STATE_DOWN'
};

const CAPABILITY_HEALTH = {
  CAPABILITY_HEALTH_STATE_HEALTHY: 'CAPABILITY_HEALTH_STATE_HEALTHY',
  CAPABILITY_HEALTH_STATE_WARNING: 'CAPABILITY_HEALTH_STATE_WARNING',
  CAPABILITY_HEALTH_STATE_DEGRADED: 'CAPABILITY_HEALTH_STATE_DEGRADED',
  CAPABILITY_HEALTH_STATE_CRITICAL: 'CAPABILITY_HEALTH_STATE_CRITICAL',
  CAPABILITY_HEALTH_STATE_DOWN: 'CAPABILITY_HEALTH_STATE_DOWN',
  CAPABILITY_HEALTH_STATE_DISABLED: 'CAPABILITY_HEALTH_STATE_DISABLED',
  CAPABILITY_HEALTH_STATE_UNSPECIFIED: 'CAPABILITY_HEALTH_STATE_UNSPECIFIED'
};

const CAPABILITY_KEYS = {
  KBGP,
  KDNS,
  KDUMMY,
  KPROXY,
  KSNMPTRAP,
  KSYNTH,
  KSYSLOG,
  LIVESYNTH,
  KTRAFFIC,
  RANGER,
  KSQUEEGEE,
  KPTR,
  KMETA
};

const CLUSTER_HEALTH = {
  CLUSTER_HEALTH_STATE_UNSPECIFIED,
  CLUSTER_HEALTH_STATE_HEALTHY,
  CLUSTER_HEALTH_STATE_WARNING,
  CLUSTER_HEALTH_STATE_DEGRADED,
  CLUSTER_HEALTH_STATE_CRITICAL,
  CLUSTER_HEALTH_STATE_DOWN
};

const GROUP_HEALTH = {
  GROUP_HEALTH_STATE_UNSPECIFIED,
  GROUP_HEALTH_STATE_HEALTHY,
  GROUP_HEALTH_STATE_WARNING,
  GROUP_HEALTH_STATE_DEGRADED,
  GROUP_HEALTH_STATE_CRITICAL,
  GROUP_HEALTH_STATE_DOWN
};

const Capabilities = {
  // maybe call Capability_Keys? replacing with above is good
  KBGP,
  KDNS,
  KDUMMY,
  KPROXY,
  KSNMPTRAP,
  KSYNTH,
  KSYSLOG,
  LIVESYNTH,
  RANGER,
  KTRAFFIC,
  SYNTHETICS,
  KSQUEEGEE,
  KPTR,
  KMETA
};

const Statuses = {
  NOT_AUTHORIZED,
  RUNNING,
  DISABLED,
  DOWN,
  WARNING,
  DEGRADED,
  CRITICAL,
  PENDING,
  UP,
  UNKNOWN
};

const GROUP_ID_PREFIX = 'group:';
const CAPABILITIES_CURRENTLY_AVAILABLE_TO_ALL = [RANGER, KDNS, LIVESYNTH]; // KSNMPTRAP, KSYSLOG, KPROXY, KMETA, and KSQUEEGEE are added by permissions
const CAPABILITIES_CURRENTLY_PLANNED: string[] = [];
const CAPABILITIES_TO_ALWAYS_REMOVE_FROM_PUBLIC = [KPACKET]; // UI only setting - do not even show these in the blocked list.
const ALWAYS_SUPPORTED_CAPABILITIES = [RANGER, KSYSLOG, KSNMPTRAP, KDUMMY, KSQUEEGEE, KSYNTH, KMETA, KPTR]; // These are distribution-agnostic so can be installed on any linux distribution

export {
  AGENT_HEALTH,
  AGENT_HEALTH_INTENTS,
  AGENT_HEALTH_LABELS,
  ALWAYS_SUPPORTED_CAPABILITIES,
  CAPABILITIES_CURRENTLY_AVAILABLE_TO_ALL,
  CAPABILITIES_CURRENTLY_PLANNED,
  CAPABILITIES_DEFAULT_CONFIGS,
  CAPABILITIES_TO_ALWAYS_REMOVE_FROM_PUBLIC,
  CAPABILITY_DESCRIPTIONS,
  CAPABILITY_HEALTH,
  CAPABILITY_HEALTH_INTENTS,
  CAPABILITY_HEALTH_LABELS,
  CAPABILITY_KEYS,
  CAPABILITY_TITLES,
  Capabilities,
  CLUSTER_HEALTH,
  CLUSTER_HEALTH_INTENTS,
  CLUSTER_HEALTH_LABELS,
  CMD_ARGS_KPROXY_KEBAB,
  CMD_ARGS_KPROXY_KEBAB_OBJ,
  collapseFlowProxyCapabilityKeys,
  FLOW_PROXY_COMBINED_DESCRIPTION,
  FLOW_PROXY_KEYS,
  FLOW_PROXY_MODE_LABELS,
  FLOW_PROXY_MODES,
  FLOW_PROXY_TITLE,
  getCapabilityKeyForFlowProxyMode,
  getFlowProxyModeForCapabilityKey,
  getFlowProxyModeTitle,
  GROUP_HEALTH,
  GROUP_HEALTH_INTENTS,
  GROUP_HEALTH_LABELS,
  GROUP_ID_PREFIX,
  isFlowProxyCapabilityKey,
  KPROXY_DISPLAY_INTERVAL,
  KPROXY_VERSION_MAP,
  Statuses
};
